import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const list = articles.filter((a) => a.category === slug);
  const others = articles.filter((a) => a.category !== slug).slice(0, 4);

  useSeo({
    title: category
      ? `${category.name}爆料汇总 | 爆瓜爆料社`
      : "分类不存在 | 爆瓜爆料社",
    description: category
      ? `爆瓜爆料社「${category.name}」专栏，${category.desc}`
      : "没有找到对应的分类栏目。",
    keywords: category ? `爆瓜爆料,${category.name},爆料汇总,每日更新` : "爆瓜爆料",
    canonical: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}爆料汇总`,
            description: category.desc,
            url: `https://app-test.cn/category/${slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://app-test.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: `https://app-test.cn/category/${slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="mx-auto max-w-[1240px] px-4 py-20 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-background-400" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          这个分类还没开张
        </h1>
        <p className="mt-2 text-sm text-foreground-500">去别的栏目逛逛，说不定有更大的瓜。</p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="w-full border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: category.name }]} />
          <div className="mt-5 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`} aria-hidden="true" />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">
                {category.desc}
              </p>
              <p className="mt-2 text-xs text-foreground-500">共 {list.length} 条精选爆料</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/80 bg-background-50 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  c.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300/80 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-10 md:px-6">
        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        ) : (
          <p className="rounded-lg border border-background-200 bg-background-100/60 p-8 text-center text-sm text-foreground-500">
            该栏目内容正在整理中，先看看其他分类吧。
          </p>
        )}

        <section className="mt-14">
          <h2 className="mb-5 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-bookmark-3-line text-accent-600" aria-hidden="true" />
            你可能也感兴趣
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((a) => (
              <ArticleCard key={a.slug} article={a} variant="vertical" showExcerpt={false} />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}